import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { StaticDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.static";
import { staticSchema } from "@-/dev.improve.haniverse.database.schemas.static.schema";
import { alias } from "@-/com.npmjs.drizzle-orm/sqlite-core";
import { and, eq, inArray, isNull } from "@-/com.npmjs.drizzle-orm";
import runes from "@-/com.npmjs.runes2";
import { useActorTableChangeVersion } from "@-/dev.improve.haniverse.lib.database.change.actor";
import { createUseQueryOptions } from "@-/dev.improve.haniverse.lib.query-options";
import { useActorUserDataQueryOptions } from "@-/dev.improve.haniverse.lib.user";
import { getHskWordSetFromDb } from "@-/dev.improve.haniverse.lib.morpheme.grade.hsk";
import { isNonEmpty } from "@-/com.npmjs.ts-array-length";

/**
 * The HSK/TBCL dataset universe that the Browse pages are scoped to, for the user's active
 * HSK version. Every list is deduplicated.
 *
 * - {@link words} — every word across all levels of the active HSK version, each split into
 *   its constituent characters so callers can decide a word is "fully known" when every
 *   character has been learned.
 * - {@link characters} — distinct characters appearing in {@link words}.
 * - {@link primitives} — distinct primitives (decomposition-only components, i.e. graphemes
 *   with no PRC tier) that appear in the {@link characters}.
 */
export interface BrowseDatasetStats {
  characters: string[];
  primitives: string[];
  words: { characters: string[]; word: string }[];
}

export function useGetBrowseDatasetStatsQueryKey() {
  const [staticDb] = useContextSelectors(StaticDatabaseContext, (c) => c.db);
  const actorUserDataQueryOptions = useActorUserDataQueryOptions();
  const withTableChangeVersion = useActorTableChangeVersion("user", {});

  return () =>
    withTableChangeVersion([
      "browseDatasetStats",
      { actorUserDataQueryOptions, staticDb },
    ] as const);
}

export const [useBrowseDatasetStatsQueryOptions, browseDatasetStatsQueryFn] = createUseQueryOptions(
  useGetBrowseDatasetStatsQueryKey,
  async ({ client, queryKey: [_, { actorUserDataQueryOptions, staticDb }] }) => {
    const actorUserData = await client.fetchQuery(actorUserDataQueryOptions);
    const hskVersion = actorUserData.user.hskVersion;

    const db = staticDb(import.meta.location);

    // 1. Words: union of every level for the active HSK version.
    const wordSet = await getHskWordSetFromDb(db, hskVersion);

    // 2. Characters: distinct constituent characters of those words.
    const characterSet = new Set<string>();
    const words = Array.from(wordSet, (word) => {
      const characters = runes(word);
      for (const character of characters) {
        characterSet.add(character);
      }
      return { characters, word };
    });
    const characters = [...characterSet];

    // 3. Primitives: decomposition-only components (PRC tier IS NULL) of those characters.
    const primitiveSet = new Set<string>();
    if (isNonEmpty(characters)) {
      const componentGrapheme = alias(staticSchema.grapheme, "component_grapheme");
      // A plain `select`, deduplicated by the `Set` below — NOT `selectDistinct`. `characters` is
      // the whole HSK universe (~1,400 glyphs), far over D1's 100-bound-parameter cap, and the
      // worker transport only splits a `where … in (…)` it can prove safe to concatenate: a
      // `distinct` cannot be preserved across chunks, so it opted this query OUT of chunking and
      // every online read failed with `D1_ERROR: too many SQL variables` (Sentry HAN-W, 52 users
      // on /browse/characters). Duplicates across chunks are harmless here; the set absorbs them.
      const primitiveRows = await db
        .select({ glyph: componentGrapheme.glyph })
        .from(staticSchema.grapheme)
        .innerJoin(
          staticSchema.graphemeComponent,
          eq(staticSchema.grapheme.id, staticSchema.graphemeComponent.graphemeId),
        )
        .innerJoin(
          componentGrapheme,
          eq(staticSchema.graphemeComponent.componentGraphemeId, componentGrapheme.id),
        )
        .leftJoin(
          staticSchema.graphemePrcData,
          eq(componentGrapheme.id, staticSchema.graphemePrcData.graphemeId),
        )
        .where(
          and(
            inArray(staticSchema.grapheme.glyph, characters),
            isNull(staticSchema.graphemePrcData.tier),
          ),
        );
      for (const row of primitiveRows) {
        primitiveSet.add(row.glyph);
      }
    }

    return { characters, primitives: [...primitiveSet], words };
  },
);

// The dataset is scoped to the active `hskVersion` on the mutable `user` row;
// the static-DB reads are immutable.
